
import { defineComponent, inject } from "vue";
import { mapGetters } from "vuex";
import { images } from "@/mixins/images";
import { Rank } from "@/store/Ranks/RanksInterfaces";
import {
  BinaryTreeNodeUser,
  UserSubStatus,
} from "functions/src/Users/UsersInterfaces";
import {
  ACADEMY_SUBSCRIPTION_IDS,
  LITE_SUBSCRIPTION_IDS,
  PKG_FLY,
  PKG_GO,
  PKG_WIN,
  VIP_SUBSCRIPTION_IDS,
} from "functions/src/Subscriptions/packageIds";
import { productVisual } from "@/components/common/productVisuals";
import {
  loadSubtree,
  expandSubtree,
  collapseSubtree,
  refreshNodePointsIfStale,
} from "@/store/BinaryTree/BinaryTreeInterfaces";
import PackageIcon from "@/components/common/PackageIcon.vue";

// Debounce for the lazy points refresh: only a node the cursor RESTS on past
// this window fetches, so sweeping across many cards fires nothing (each card's
// timer is cleared on mouseleave / popover close before it elapses).
const POINTS_REFRESH_DEBOUNCE_MS = 350;

// Puntos con separador de miles del navegador — `toLocaleString()` SIN locale
// fijo, igual que el resto de la aplicación (BinaryNetwork.vue, TeamMember.vue).
//
// Se escriben ENTEROS, sin abreviar: una primera versión los recortaba a «988K»
// por miedo al ancho, y la abreviatura sólo restaba legibilidad.
//
// Devuelve "0" ante cualquier valor no finito, para que un nodo sin datos de
// puntos —la raíz recién creada, uno cuyo sumario aún no existe— muestre un cero
// coherente en vez de «undefined» o «NaN».
function formatPoints(value: number): string {
  return Number.isFinite(value) ? Math.trunc(value).toLocaleString() : "0";
}

// A partir de cuántos caracteres una cifra deja de caber en su media tarjeta y
// hay que encogerla. Medido en Chromium con la tipografía real de la aplicación
// (Schibsted Grotesk) y el ancho real de la columna, que son 42px:
//
//   «770,700»    7 caracteres   36,1px  → cabe de sobra (es el caso REAL de hoy)
//   «999,999»    7 caracteres   38,0px  → cabe
//   «1,000,000»  9 caracteres   45,0px  → NO cabe a tamaño normal
//   «12,345,678» 10 caracteres  48,8px  → NO cabe a tamaño normal
//
// Los saltos van de 7 a 9 caracteres porque el separador de miles entra de golpe
// («999,999» → «1,000,000»), así que el umbral en 7 no deja ningún caso a medias.
// Por encima de él se pinta con `.otn-pts-val-long`, que baja de 0,6rem a 0,5rem
// y devuelve esas dos cifras a 37,5px y 40,7px — dentro de los 42px. Encoger es
// preferible a recortar con puntos suspensivos: un número a medias («1,000,…»)
// no se lee mal, se lee MAL ENTENDIDO.
const PTS_MAX_CHARS = 7;

function pointsSizeClass(text: string): string {
  return text.length > PTS_MAX_CHARS ? "otn-pts-val-long" : "";
}

export default defineComponent({
  name: "org-tree-node",
  components: { PackageIcon },
  props: {
    username: { type: String, required: true },
    // Levels revealed per expansion click — bounded, never the whole line.
    expandDepth: { type: Number, default: 2 },
  },
  data() {
    return {
      loading: false,
      profilePic: "",
      // Touch: the details popover is toggled by tapping the info button
      // (no hover on mobile). Desktop shows it on card hover instead.
      showPopover: false,
      // Pending debounce timer for the lazy points refresh (per node instance).
      pointsRefreshTimer: null as ReturnType<typeof setTimeout> | null,
    };
  },
  computed: {
    ...mapGetters("ranks", ["rankById"]),
    ...mapGetters("binaryTree", ["nodeFor", "isExpanded"]),
    // Expansion lives in the store (keyed by username) so it survives re-root
    // and route changes, and is restored when returning to an open tree.
    expanded(): boolean {
      return this.isExpanded(this.username);
    },
    node(): BinaryTreeNodeUser {
      return (
        (this.nodeFor(this.username) as BinaryTreeNodeUser) ||
        new BinaryTreeNodeUser(this.username)
      );
    },
    leftUsername(): string {
      return this.childUsername(this.node.childLeft);
    },
    rightUsername(): string {
      return this.childUsername(this.node.childRight);
    },
    hasChildren(): boolean {
      return this.leftUsername !== "" || this.rightUsername !== "";
    },
    // Direct children's data is in the store (empty leg counts as present).
    childrenPresent(): boolean {
      const ok = (u: string) => u === "" || !!this.nodeFor(u);
      return ok(this.leftUsername) && ok(this.rightUsername);
    },
    UserRank(): Rank | undefined {
      return this.node.rank
        ? (this.rankById(this.node.rank) as Rank)
        : undefined;
    },
    remainingDays(): number {
      // La ACADEMIA manda sobre el LITE; dentro de cada una, la generación que
      // más lejos llegue. Buscando sólo `trading-academy` un traducido enseñaría
      // el documento VIEJO, vencido: la tarjeta se apagaría (`otn-inactive`) y
      // la píldora saldría en gris teniendo `gtr-fly` al día.
      const subs = this.node.subscriptions || new Array<UserSubStatus>();
      const bestOf = (family: readonly string[]): number => {
        const days = subs
          .filter(
            (s) =>
              family.includes(s.subscriptionId) &&
              s.isActive &&
              (s.remainingDays ?? 0) > 0
          )
          .map((s) => s.remainingDays as number);
        return days.length > 0 ? Math.floor(Math.max(...days)) : 0;
      };
      return bestOf(ACADEMY_SUBSCRIPTION_IDS) || bestOf(LITE_SUBSCRIPTION_IDS);
    },
    statusClass(): string {
      // day status lives in the days pill; the card only marks inactive (to dim)
      return this.remainingDays <= 0 ? "otn-inactive" : "";
    },
    daysClass(): string {
      const d = this.remainingDays;
      if (d <= 0) return "bg-secondary";
      if (d < 5) return "bg-danger";
      if (d < 10) return "bg-warning text-dark";
      return "bg-success";
    },
    pendingLeft(): number {
      return this.node.points?.left?.pending ?? 0;
    },
    pendingRight(): number {
      return this.node.points?.right?.pending ?? 0;
    },
    pendingLeftText(): string {
      return formatPoints(this.pendingLeft);
    },
    pendingRightText(): string {
      return formatPoints(this.pendingRight);
    },
    pendingLeftSizeClass(): string {
      return pointsSizeClass(this.pendingLeftText);
    },
    pendingRightSizeClass(): string {
      return pointsSizeClass(this.pendingRightText);
    },
    // Tooltip del bloque: el rótulo del nodo va abreviado por espacio, así que
    // aquí se escribe el vocabulario completo que usa la vista Red Binaria.
    pointsTitle(): string {
      return (
        `Puntos pendientes — Equipo Izquierdo: ${this.pendingLeftText} · ` +
        `Equipo Derecho: ${this.pendingRightText}`
      );
    },
    isVIP(): boolean {
      const subs = this.node.subscriptions;
      return !!subs && subs.some((s) => s.subscriptionId === "mmi-colombia");
    },
    isLite(): boolean {
      // «Es Lite» = no tiene NINGUNA academia vigente y sí un Lite vigente, en
      // cualquiera de las dos generaciones de cada familia. Mirando sólo los ids
      // viejos, un traducido con `gtr-fly` vigente pasaría el primer filtro y se
      // marcaría como Lite.
      const subs = this.node.subscriptions || new Array<UserSubStatus>();
      const anyActiveIn = (family: readonly string[]): boolean =>
        subs.some((s) => family.includes(s.subscriptionId) && !!s.isActive);
      if (anyActiveIn(ACADEMY_SUBSCRIPTION_IDS)) return false;
      return anyActiveIn(LITE_SUBSCRIPTION_IDS);
    },
    /**
     * EL NIVEL DEL SOCIO, para que `PackageIcon` elija dibujo y color.
     *
     * Es la MISMA escalera y el mismo orden que ya aplica `BinaryNode.vue`, y
     * está copiada a propósito en vez de compartida: los dos componentes pintan
     * el mismo árbol con dos motores distintos, y lo único que tienen en común
     * es la forma del nodo. Si algún día se unifican, esto se va con ellos.
     *
     *   · WIN gana a todo. Quien tiene el documento de WIN tiene ADEMÁS el de
     *     academia, así que preguntar primero por la academia devolvería FLY
     *     siempre y el diamante no saldría nunca.
     *   · La academia gana al Lite — es la misma precedencia que ya usa
     *     `isLite` para el color de la tarjeta y para los días.
     *   · Sin ninguno de los tres, FLY: es el reparto de siempre y lo que este
     *     árbol daba por hecho antes de que existieran tres niveles.
     */
    UserPackageVisual(): string {
      const subs = this.node?.subscriptions || new Array<UserSubStatus>();
      const hasActive = (ids: readonly string[]) =>
        subs.some((s) => ids.includes(s.subscriptionId) && s.isActive === true);
      if (hasActive(VIP_SUBSCRIPTION_IDS)) return PKG_WIN;
      const base = this.isLite ? PKG_GO : PKG_FLY;
      // EL ACELERADOR SUBE DE FLY A WIN, PERO NO SUBE A UN GO.
      //
      // Regla de negocio (Hector, 2026-07-28), y la asimetría es el fondo del
      // asunto: WIN **es** la academia MÁS el Acelerador, así que quien tiene
      // FLY vigente y el Acelerador vigente ya tiene lo que WIN vende — pintarle
      // el cohete le enseña un escalón por debajo del que ocupa. Un GO con
      // Acelerador NO es eso: le falta la academia, que es la otra mitad. Sigue
      // siendo GO con un producto aparte, y el diamante mentiría.
      //
      // Se exige VIGENTE (`isActive`) en los dos lados. `isVIP`, el que enciende
      // el dorado de la tarjeta, mira sólo si el documento EXISTE — para un
      // adorno pasa, para decidir qué escalón se enseña no.
      if (base === PKG_FLY && hasActive(["mmi-colombia"])) return PKG_WIN;
      return base;
    },
    // El nombre comercial, para el `title` del icono. Sale del catálogo visual
    // y no de un literal: el día que WIN se llame otra cosa, cambia en el único
    // sitio donde vive el nombre y esta tarjeta se entera sola.
    packageTitle(): string {
      return productVisual(this.UserPackageVisual).label;
    },
  },
  methods: {
    childUsername(
      child: BinaryTreeNodeUser | string | null | undefined
    ): string {
      if (child === null || child === undefined) return "";
      if (typeof child === "string") return child;
      return child.username || "";
    },
    toggleInfo(): void {
      this.showPopover = !this.showPopover;
      // Mobile/tap path into the SAME lazy refresh as desktop hover: opening the
      // popover schedules a debounced refresh, closing it cancels a pending one.
      if (this.showPopover) this.schedulePointsRefresh();
      else this.cancelPointsRefresh();
    },
    // Debounced hook shared by hover (mouseenter) and tap (toggleInfo). After the
    // debounce, ask the store to refresh THIS node's points only if its cache is
    // stale; the store dedupes and TTL-gates.
    //
    // Sigue teniendo sentido aunque los puntos ya NO vivan en el popover: son la
    // única forma de refrescar un nodo suelto sin recargar la rama, y ahora el
    // resultado se ve directamente en la tarjeta (`.otn-pts`) en vez de dentro de
    // la tarjeta flotante.
    schedulePointsRefresh(): void {
      this.cancelPointsRefresh();
      this.pointsRefreshTimer = setTimeout(() => {
        this.pointsRefreshTimer = null;
        this.$store.dispatch(
          "binaryTree/" + refreshNodePointsIfStale,
          this.username
        );
      }, POINTS_REFRESH_DEBOUNCE_MS);
    },
    cancelPointsRefresh(): void {
      if (this.pointsRefreshTimer !== null) {
        clearTimeout(this.pointsRefreshTimer);
        this.pointsRefreshTimer = null;
      }
    },
    async toggle(): Promise<void> {
      this.showPopover = false;
      // A click closes the popover, so drop any pending hover/tap refresh.
      this.cancelPointsRefresh();
      if (!this.hasChildren) return;
      if (this.expanded) {
        this.$store.dispatch("binaryTree/" + collapseSubtree, this.username);
        return;
      }
      // Reveal `expandDepth` levels of descendants below this node. The backend
      // returns `levels` deep counting this node, so `levels` of descendants
      // need `expandDepth + 1`; a node loaded with L levels already has L - 1
      // descendant levels present, so only fetch when that is not enough.
      const needed = this.expandDepth;
      const have = (this.node.loadedLevels || 0) - 1;
      if (have < needed && !(await this.fetchSubtree(needed + 1))) return;
      this.$store.dispatch("binaryTree/" + expandSubtree, {
        username: this.username,
        levels: needed,
      });
    },
    // When a node is already expanded (e.g. restored from saved state) but its
    // children aren't loaded yet, fetch them so the open branch renders. Deeper
    // open nodes cascade the same way as they mount.
    async ensureChildrenLoaded(): Promise<void> {
      if (!this.hasChildren || this.childrenPresent) return;
      await this.fetchSubtree(this.expandDepth + 1);
    },
    async fetchSubtree(levels: number): Promise<boolean> {
      if (this.loading) return false;
      this.loading = true;
      try {
        await this.$store.dispatch("binaryTree/" + loadSubtree, {
          username: this.username,
          levels,
        });
        return true;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error cargando la línea de", this.username, error);
        return false;
      } finally {
        this.loading = false;
      }
    },
    reroot(): void {
      this.rerootTree(this.username);
    },
    loadPic(): void {
      const url = this.node.pictureUrl;
      if (url) {
        this.getProfilePicture(url).then((response: string) => {
          this.profilePic = response;
        });
      }
    },
  },
  watch: {
    expanded: {
      immediate: true,
      handler(open: boolean): void {
        if (open) this.ensureChildrenLoaded();
      },
    },
  },
  mounted(): void {
    const el = this.$refs.card as HTMLElement | undefined;
    if (el) this.cardObserver.register(el, this.loadPic);
  },
  beforeUnmount(): void {
    this.cancelPointsRefresh();
    const el = this.$refs.card as HTMLElement | undefined;
    if (el) this.cardObserver.unregister(el);
  },
  setup() {
    // Provided by BinaryTreeView.vue; re-roots the whole chart on the given user.
    const rerootTree = inject<(username: string) => void>(
      "binaryTreeReroot",
      () => undefined
    );
    // Shared viewport observer: defers profile-image loading until the card has
    // dwelled in view, so large trees don't hammer Storage with downloads.
    const cardObserver = inject<{
      register: (el: Element, onDwell: () => void) => void;
      unregister: (el: Element) => void;
    }>("binaryTreeCardObserver", {
      register: () => undefined,
      unregister: () => undefined,
    });
    return {
      getProfilePicture: images.methods.getProfilePicture,
      rerootTree,
      cardObserver,
    };
  },
});
